import { jsx, jsxs } from 'react/jsx-runtime';
import { FieldMessage } from '@ds/field-message';
import { Fieldset } from '@ds/utils';
import classNames from 'classnames';
import React, { useId } from 'react';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise, SuppressedError, Symbol, Iterator */


function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

var styles = {"radioGroup":"radioGroup___swK8M","radioGroup--horizontal":"radioGroup--horizontal___8YnWD","radioGroup__item":"radioGroup__item___5gbmn"};

/**
 * Groups radio buttons together and lays them out vertically or horizontally.
 *
 * @example
 *
 * import { RadioGroup } from '@ds/radio-group';
 * import { Radio } from '@ds/radio';
 *
 * <RadioGroup groupTitle="Select transport method" name="transport-method">
 *   <Radio>Bus</Radio>
 *   <Radio>Train</Radio>
 *   <Radio>Tram</Radio>
 * </RadioGroup>
 */
const RadioGroup = props => {
  var _a, _b;
  const {
      children,
      direction = 'vertical',
      fieldMessage,
      groupTitle,
      hideGroupTitle = false,
      id,
      invalid,
      radioName,
      slotProps
    } = props,
    rest = __rest(props, ["children", "direction", "fieldMessage", "groupTitle", "hideGroupTitle", "id", "invalid", "radioName", "slotProps"]);
  const fieldMessageFallbackId = 'field-message-' + useId();
  const fieldMessageId = id || fieldMessageFallbackId;
  const childrenInList = radioName ? React.Children.map(children, child => /*#__PURE__*/React.isValidElement(child) && jsx("li", {
    className: styles.radioGroup__item,
    children:
    /*#__PURE__*/
    // @ts-expect-error: 'name' does not exist in type
    React.cloneElement(child, {
      name: radioName
    })
  })) : React.Children.map(children, child => /*#__PURE__*/React.isValidElement(child) && jsx("li", {
    className: styles.radioGroup__item,
    children: child
  }));
  const radioGroupClassNames = classNames(styles.radioGroup, (_a = slotProps === null || slotProps === void 0 ? void 0 : slotProps.group) === null || _a === void 0 ? void 0 : _a.className, {
    [styles['radioGroup--horizontal']]: direction === 'horizontal'
  });
  return jsxs(Fieldset, Object.assign({
    "aria-describedby": fieldMessage ? fieldMessageId : undefined,
    visuallyHideLegend: hideGroupTitle,
    legend: groupTitle
  }, rest, {
    children: [jsx("ul", {
      className: radioGroupClassNames,
      children: childrenInList
    }), fieldMessage && jsx(FieldMessage, {
      className: (_b = slotProps === null || slotProps === void 0 ? void 0 : slotProps.fieldMessage) === null || _b === void 0 ? void 0 : _b.className,
      id: fieldMessageId,
      role: invalid ? 'alert' : undefined,
      skin: invalid ? 'error' : undefined,
      children: fieldMessage
    })]
  }));
};

export { RadioGroup };
